import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { ACCOUNT_SETTINGS_PAGES } from '../../routing/routeConfiguration';
import { isFactoryUser, isAgentUser, isBuyerUser } from '../../util/userHelpers';
import { LinkTabNavHorizontal } from '../../components';

import css from './UserNav.module.css';

/**
 * A component that renders a navigation bar for user-specific pages.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} props.currentPage - The current page (e.g. 'ManageListingsPage')
 * @param {boolean} [props.showManageListingsLink] - Whether to show the manage listings link
 * @param {*} [props.currentUser] - Current user from Redux store
 * @returns {JSX.Element} User navigation component
 */
const UserNav = props => {
  const { className, rootClassName, currentPage, showManageListingsLink, currentUser } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  const manageListingsTabMaybe = showManageListingsLink
    ? [
        {
          text: <FormattedMessage id="UserNav.yourListings" />,
          selected: currentPage === 'ManageListingsPage',
          linkProps: {
            name: 'ManageListingsPage',
          },
        },
      ]
    : [];

  const factoryTabsMaybe = isFactoryUser(currentUser)
    ? [
        {
          text: <FormattedMessage id="UserNav.productImport" />,
          selected: currentPage === 'ProductImportPage',
          disabled: false,
          linkProps: {
            name: 'ProductImportPage',
          },
        },
        {
          text: <FormattedMessage id="UserNav.myProducts" />,
          selected: currentPage === 'FactoryProductsPage',
          disabled: false,
          linkProps: {
            name: 'FactoryProductsPage',
          },
        },
      ]
    : [];

  const agentTabsMaybe = isAgentUser(currentUser)
    ? [
        {
          text: <FormattedMessage id="UserNav.agentDashboard" />,
          selected: currentPage === 'AgentDashboardPage',
          disabled: false,
          linkProps: {
            name: 'AgentDashboardPage',
            params: { tab: 'dashboard' },
          },
        },
      ]
    : [];

  const buyerTabsMaybe = isBuyerUser(currentUser)
    ? [
        {
          text: <FormattedMessage id="UserNav.buyerDashboard" />,
          selected: currentPage === 'BuyerDashboardPage',
          disabled: false,
          linkProps: {
            name: 'BuyerDashboardPage',
            params: { tab: 'dashboard' },
          },
        },
        {
          text: <FormattedMessage id="UserNav.catalogue" />,
          selected: currentPage === 'CataloguePage',
          linkProps: { name: 'CataloguePage' },
        },
        {
          text: <FormattedMessage id="UserNav.myQuotes" />,
          selected: false,
          linkProps: { name: 'BuyerDashboardPage', params: { tab: 'quotations' } },
        },
        {
          text: <FormattedMessage id="UserNav.customRfq" />,
          selected: currentPage === 'RfqPage',
          linkProps: { name: 'RfqPage' },
        },
      ]
    : [];

  const tabs = [
    ...manageListingsTabMaybe,
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      selected: currentPage === 'ProfileSettingsPage',
      disabled: false,
      linkProps: {
        name: 'ProfileSettingsPage',
      },
    },
    {
      text: <FormattedMessage id="UserNav.accountSettings" />,
      selected: ACCOUNT_SETTINGS_PAGES.includes(currentPage),
      disabled: false,
      linkProps: {
        name: 'ContactDetailsPage',
      },
    },
    ...factoryTabsMaybe,
    ...agentTabsMaybe,
    ...buyerTabsMaybe,
  ];

  return (
    <LinkTabNavHorizontal
      className={classes}
      tabRootClassName={css.tab}
      tabs={tabs}
      skin="dark"
      ariaLabel={intl.formatMessage({ id: 'UserNav.screenreader.userNav' })}
    />
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(UserNav);
